import { combineAccessInputs, mapAccessInput, type AccessInput } from '@/abilities/accessInput';
import { createCaseAbility } from '@/abilities/createCaseAbility';
import { treeDestinationAccess, type DestinationAccess } from '@/abilities/destination';
import { companySettingsBankFeedRules } from '@/abilities/companySettings/rules/bankFeed';
import {
  companySettingsDestinationTree,
  toCompanySettingsDestination,
  type CompanySettingsBankFeedDestinationKey,
  type CompanySettingsDestination,
} from '@/abilities/companySettings/vocabulary';
import {
  companyBankFeedAccessContext,
  type CompanyBankFeedAccessContext,
  type CompanyBankFeedAccessValues,
} from '@/abilities/companyBankFeed/context';
import { companyBankFeedRules } from '@/abilities/companyBankFeed/rules';
import {
  companyBankFeedDestinationTree,
  toCompanyBankFeedDestination,
  type CompanyBankFeedAction,
  type CompanyBankFeedDestination,
  type CompanyBankFeedDestinationKey,
} from '@/abilities/companyBankFeed/vocabulary';

/**
 * How the user may reach each destination of the Bank Feed page, and the Bank Feed destinations in settings the page's shortcuts
 * lead to — the latter from the settings rules themselves, so a shortcut and the settings route never disagree. The
 * only answer the case gives: a CASL check on the page itself would find no rule and always say no.
 */
export type CompanyBankFeedAccess = {
  readonly accessOf: (destination: CompanyBankFeedDestinationKey) => DestinationAccess;
  readonly settingsAccessOf: (destination: CompanySettingsBankFeedDestinationKey) => DestinationAccess;
};

export type CompanyBankFeedAccessInputs = {
  readonly [Key in keyof CompanyBankFeedAccessValues]: AccessInput<CompanyBankFeedAccessValues[Key]>;
};

export const companyBankFeedAccessOf = (ctx: CompanyBankFeedAccessContext): CompanyBankFeedAccess => {
  const ability = createCaseAbility<CompanyBankFeedAction, CompanyBankFeedDestination | CompanySettingsDestination>([
    ...companyBankFeedRules(ctx),
    ...companySettingsBankFeedRules(ctx),
  ]);
  return {
    accessOf: treeDestinationAccess(ability, toCompanyBankFeedDestination, companyBankFeedDestinationTree),
    settingsAccessOf: treeDestinationAccess(ability, toCompanySettingsDestination, companySettingsDestinationTree),
  };
};

export const companyBankFeedAccess = (inputs: CompanyBankFeedAccessInputs): AccessInput<CompanyBankFeedAccess> =>
  mapAccessInput(combineAccessInputs(inputs), (values) =>
    companyBankFeedAccessOf(companyBankFeedAccessContext(values)),
  );
